import { CSSProperties } from 'react';

import { Flag32 as Flag } from '@weston/react-world-flags';
import isoCountries from 'i18n-iso-countries';
import isoCountriesEN from 'i18n-iso-countries/langs/en.json';
import isoCountriesFR from 'i18n-iso-countries/langs/fr.json';
import { useSnapshot } from 'valtio';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import dayjs from '@/dayjs';

import { GetBookingsExpandedQuery } from '@/graphql/GetBookingsExpanded.generated';

import { useFormatters } from '@/hooks';

import { appStore } from '@/store';

const { VITE_APP_DEFAULT_LOCALE } = import.meta.env;

// for now we only support country names in english and french
isoCountries.registerLocale(isoCountriesEN);
isoCountries.registerLocale(isoCountriesFR);

export interface TripItemProps {
  booking: GetBookingsExpandedQuery['container_insights_bookings_expanded'][0];
  style: CSSProperties;
}

function TripItem(props: TripItemProps) {
  const appSnap = useSnapshot(appStore);
  const { booking, style } = props;
  const { formatNumber } = useFormatters();

  const ata = dayjs(booking.end_datetime);

  // en-US -> en
  const isoCountriesLocale = (
    appSnap.user ? appSnap.user.locale : VITE_APP_DEFAULT_LOCALE
  ).split('-')[0];

  return (
    <div style={style}>
      <ListItem
        sx={{
          paddingLeft: 0,
          paddingRight: 2,
        }}
      >
        <Stack
          direction="row"
          flexGrow={1}
          sx={{
            overflowX: 'auto',
          }}
        >
          <Stack direction="column" spacing={0.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
              flexGrow={1}
            >
              <Tooltip
                title={`${booking.start_long_location}, ${isoCountries.getName(
                  booking.start_iso as string,
                  isoCountriesLocale,
                )}`}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography noWrap>{booking.start_location}</Typography>
                  <Box sx={{ pt: 0.5 }}>
                    <Flag code={booking.start_iso as string} height={14} />
                  </Box>
                </Stack>
              </Tooltip>
              <ArrowRightAltIcon />

              <Tooltip
                title={`${booking.end_long_location}, ${isoCountries.getName(
                  booking.end_iso as string,
                  isoCountriesLocale,
                )}`}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography noWrap>{booking.end_location}</Typography>
                  <Box sx={{ pt: 0.5 }}>
                    <Flag code={booking.end_iso as string} height={14} />
                  </Box>
                </Stack>
              </Tooltip>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
            >
              <Chip
                color="primary"
                variant="outlined"
                size="small"
                label={`ATA: ${ata.format('ll')}`}
                sx={{
                  borderRadius: 1,
                }}
              />
              <Chip
                color="secondary"
                variant="outlined"
                size="small"
                label={
                  booking.co2eq_kg
                    ? `CO₂e: ${formatNumber(booking.co2eq_kg)} kg`
                    : `CO₂e: N/A`
                }
                sx={{
                  borderRadius: 1,
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        {/* <IconButton>
          <ChevronRightIcon />
        </IconButton> */}
      </ListItem>
      <Divider />
    </div>
  );
}

export { TripItem };
